// Cookie consent state shared by the consent window and the Google Analytics loader.
// The choice lives in localStorage as {a, t, v}: analytics flag, timestamp, policy version.
import { SITE } from "@/site";

export const CONSENT_STORAGE_KEY = "rar_cc";
export const CONSENT_ID_KEY = "rar_consent_id";
// Bump to ask everyone again (e.g. when a new tag category is added)
// 1.2 adds Microsoft Clarity, only on sites that have a clarityId
export const POLICY_VERSION = SITE.clarityId ? "1.2" : "1.1";
// A saved choice is valid for 6 months, then the window asks again
export const CHOICE_MAX_AGE_MS = 182 * 24 * 60 * 60 * 1000;
// Fired on window with the new choice as `detail` whenever the visitor saves one
export const CONSENT_CHANGE_EVENT = "rar:consent-change";
// Fired on window to reopen the consent window (footer link)
export const OPEN_PREFS_EVENT = "rar:cookie-prefs";

export type ConsentChoice = { a: 0 | 1 };

/** Parses a stored choice; null when missing, malformed, expired or made under another policy version. */
export function parseChoice(raw: string | null, now: number): ConsentChoice | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as { a?: unknown; t?: unknown; v?: unknown };
    if (typeof stored.t !== "number" || now - stored.t > CHOICE_MAX_AGE_MS || stored.v !== POLICY_VERSION) return null;
    return { a: stored.a ? 1 : 0 };
  } catch {
    return null;
  }
}

export function serializeChoice(choice: ConsentChoice, now: number): string {
  return JSON.stringify({ ...choice, t: now, v: POLICY_VERSION });
}

export function readChoice(): ConsentChoice | null {
  try {
    return parseChoice(localStorage.getItem(CONSENT_STORAGE_KEY), Date.now());
  } catch {
    return null;
  }
}
